import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, Input } from 'antd';
import { useAntdAppMessage } from '../../hooks/useAntdAppMessage';
import { CloseOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { UserApi, Configuration } from '../../service';
import CustomButton, { RevButton } from '../Button';
import styles from './ShareModal.module.scss';
import { getActiveShareEmails } from '../../utils/userPresence';
import { DeletedUserChipAvatar } from '../UserPresence/DeletedUserAvatarShell';
import { useAuth } from '../../hooks/useAuth';
import { useShareSuggestionDeletedEmails } from '../../hooks/useShareSuggestionDeletedEmails';

interface ShareModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (shareList: string[]) => Promise<void>;
  loading: boolean;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any; path?: string }>;
  fileDetail?: any;
  currentEntry?: any;
  variant?: 'internal' | 'external';
}

function isValidEmail(str: string): boolean {
  const s = String(str || '').trim();
  return s.length > 0 && s.includes('@');
}

const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading,
  selectedItems,
  selectedItemsDetails,
  fileDetail,
  currentEntry,
  variant = 'internal'
}) => {
  const message = useAntdAppMessage();
  const [share, setShare] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [shareOptions, setShareOptions] = useState<{ label: string; value: string }[]>([]);
  const [shareSearchLoading, setShareSearchLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const userApi = useMemo(() => new UserApi(new Configuration()), []);
  const { canSearchUserEmails, user } = useAuth();
  const sharePools = useMemo(
    () => [
      fileDetail?.share,
      currentEntry?.share,
      ...(selectedItemsDetails?.map((item) => item.share) ?? []),
    ],
    [fileDetail?.share, currentEntry?.share, selectedItemsDetails]
  );
  const { filterSuggestionEmails } = useShareSuggestionDeletedEmails(
    visible,
    userApi,
    user?.role,
    sharePools
  );

  // Dropdown'u imleç (input) hizasından başlat
  useEffect(() => {
    if (!dropdownVisible || !wrapperRef.current || !inputRef.current) return;
    const wrapper = wrapperRef.current;
    const input = inputRef.current;
    const wrapperRect = wrapper.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();
    setDropdownLeft(inputRect.left - wrapperRect.left);
  }, [dropdownVisible, inputValue, share.length]);

  useEffect(() => {
    if (visible) {
      let currentShare: string[] = [];
      if (selectedItems && selectedItems.size > 0) {
        const firstSelectedItem = selectedItemsDetails?.[0];
        if (firstSelectedItem?.share) {
          currentShare = getActiveShareEmails(firstSelectedItem.share);
        }
      } else if (fileDetail?.share) {
        currentShare = getActiveShareEmails(fileDetail.share);
      } else if (currentEntry?.share) {
        currentShare = getActiveShareEmails(currentEntry.share);
      }
      setShare(currentShare);
      setInputValue('');
      setShareOptions([]);
      setDropdownVisible(false);
    }
  }, [visible, selectedItems, selectedItemsDetails, fileDetail, currentEntry]);

  const handleShareSearch = useCallback(async (value: string) => {
    const term = String(value || '').trim();
    if (!term) {
      setShareOptions([]);
      return;
    }
    if (!canSearchUserEmails) {
      setShareOptions([]);
      return;
    }
    setShareSearchLoading(true);
    try {
      const response = await userApi.apiUserEmailsGet({ searchTerm: term }, { credentials: 'include' });
      if (response.success && response.data?.emails) {
        const emails = filterSuggestionEmails(
          response.data.emails.filter(
            (email: string) => email && typeof email === 'string' && email.trim()
          )
        );
        setShareOptions(emails.map((email: string) => ({ label: email, value: email })));
      } else {
        setShareOptions([]);
      }
    } catch {
      setShareOptions([]);
    } finally {
      setShareSearchLoading(false);
    }
  }, [userApi, canSearchUserEmails, filterSuggestionEmails]);

  useEffect(() => {
    if (!inputValue.trim()) {
      setShareOptions([]);
      setDropdownVisible(false);
      return;
    }
    const t = setTimeout(() => {
      handleShareSearch(inputValue);
      setDropdownVisible(true);
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, handleShareSearch]);

  const addEmail = useCallback((email: string) => {
    const e = String(email || '').trim();
    if (!e || share.includes(e)) return;
    setShare((prev) => [...prev, e]);
    setInputValue('');
    setDropdownVisible(false);
    setShareOptions([]);
  }, [share]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        if (shareOptions.some((o) => o.value.toLowerCase() === trimmed.toLowerCase())) {
          addEmail(trimmed);
        } else if (isValidEmail(trimmed)) {
          addEmail(trimmed);
        }
      }
    }
  };

  const handleSelectOption = (email: string) => {
    addEmail(email);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(ev.target as Node)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleConfirm = async () => {
    try {
      await onConfirm(share);
    } catch {
      // Error handling is done in parent component
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      message.error('Failed to copy link.');
    }
  };

  const shareLink = useMemo(() => {
    const path =
      (selectedItems && selectedItems.size > 0 && selectedItemsDetails?.[0]?.path) ||
      fileDetail?.path ||
      currentEntry?.path;
    if (!path || typeof path !== 'string') return '';
    const normalized = path.trim().startsWith('/') ? path.trim() : `/${path.trim()}`;
    return `${window.location.origin}${normalized}`;
  }, [selectedItems, selectedItemsDetails, fileDetail, currentEntry]);

  return (
    <Modal
      centered
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Assign</span>
          <CloseOutlined onClick={onCancel} className={styles.closeIcon} />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      className={`${styles.shareModal} ${variant === 'external' ? styles.shareModalExternal : ''}`}
      width={750}
    >
      <div className={styles.modalContent}>
        {/* To-style input: chips + input (alttaki alan stili) */}
        <div className={styles.shareInputSection} ref={wrapperRef}>
          <div className={styles.toStyleInput}>
            <div className={styles.emailTags}>
              {share.map((email) => (
                <div key={email} className={styles.emailTag}>
                  <span className={styles.emailIcon}>
                    <DeletedUserChipAvatar deleted={false} sizePx={28}>
                      <UserOutlined />
                    </DeletedUserChipAvatar>
                  </span>
                  <span>{email}</span>
                  <span
                    className={styles.removeTag}
                    onClick={() => setShare(share.filter((e) => e !== email))}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
            <input
              ref={inputRef}
              type="text"
              className={styles.toStyleInputField}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={share.length === 0 ? 'Add email(s)' : ''}
            />
          </div>
          {dropdownVisible && (inputValue.trim() || shareOptions.length > 0) && (
            <div className={styles.dropdown} style={{ left: dropdownLeft }}>
              <div
                className={styles.dropdownScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                {shareSearchLoading ? (
                  <div className={styles.dropdownItem}>Loading...</div>
                ) : shareOptions.length === 0 ? (
                  <div className={styles.dropdownItem}>No email found</div>
                ) : (
                  <div className={styles.dropdownChips}>
                    {shareOptions
                      .filter((o) => !share.includes(o.value))
                      .map((o) => (
                        <div
                          key={o.value}
                          className={styles.dropdownItemChip}
                          onClick={() => handleSelectOption(o.value)}
                        >
                          <span className={styles.emailIcon}>
                            <UserOutlined />
                          </span>
                          {o.label}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Link - read-only */}
        <div className={styles.linkSection}>
          <Input
            readOnly
            value={shareLink}
            className={styles.linkInput}
            suffix={
              <span className={styles.linkSuffix}>
                {linkCopied && <span className={styles.linkCopiedText}>Link copied</span>}
                <LinkOutlined className={styles.linkInputIcon} />
              </span>
            }
          />
        </div>

        <div className={styles.actionButtons}>
          <RevButton
            icon={<LinkOutlined />}
            onClick={handleCopyLink}
            disabled={!shareLink}
            className={styles.copyLinkButton}
          >
            Copy Link
          </RevButton>
          <CustomButton onClick={handleConfirm} loading={loading} className={styles.shareButton}>
            Done
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal; 